import * as RDialog from '@radix-ui/react-dialog'
import * as RMenu from '@radix-ui/react-dropdown-menu'
import * as RPopover from '@radix-ui/react-popover'
import * as RTabs from '@radix-ui/react-tabs'
import * as RTooltip from '@radix-ui/react-tooltip'
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from './button'
import { Textarea } from './field'

/** Our dialogs and drawers open from state, not a Radix trigger, so Radix has no trigger to return focus to on
 *  close and focus fell to the page. Remember what had focus when it opened and give it back on close. */
function useReturnFocus(open: boolean) {
  const opener = useRef<HTMLElement | null>(null)
  useLayoutEffect(() => {
    if (open && document.activeElement instanceof HTMLElement && document.activeElement !== document.body) opener.current = document.activeElement
  }, [open])
  return (e: Event) => {
    if (opener.current?.isConnected) {
      e.preventDefault()
      opener.current.focus()
    }
  }
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = 'md',
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  size?: 'sm' | 'md' | 'lg' | 'xl'
}) {
  const w = { sm: 'max-w-md', md: 'max-w-xl', lg: 'max-w-3xl', xl: 'max-w-5xl' }[size]
  const returnFocus = useReturnFocus(open)
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-ink-950/40 backdrop-blur-[2px] data-[state=open]:animate-fade-in" />
        <RDialog.Content
          onCloseAutoFocus={returnFocus}
          className={cn(
            'fixed top-[6vh] left-1/2 z-50 flex max-h-[88vh] w-[calc(100vw-2rem)] -translate-x-1/2 flex-col rounded-2xl bg-surface shadow-[var(--shadow-pop)] data-[state=open]:animate-fade-in',
            w,
          )}
        >
          <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-5 py-4">
            <div>
              <RDialog.Title className="text-base font-semibold text-ink-900">{title}</RDialog.Title>
              {description ? (
                <RDialog.Description className="mt-0.5 text-[13px] text-ink-500">{description}</RDialog.Description>
              ) : (
                <RDialog.Description className="sr-only">{typeof title === 'string' ? title : 'Dialog'}</RDialog.Description>
              )}
            </div>
            <RDialog.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </RDialog.Close>
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-5 py-3">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

export function Drawer({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  width = 'max-w-xl',
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: ReactNode
  description?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  width?: string
}) {
  const returnFocus = useReturnFocus(open)
  return (
    <RDialog.Root open={open} onOpenChange={onOpenChange}>
      <RDialog.Portal>
        <RDialog.Overlay className="fixed inset-0 z-50 bg-ink-950/30 data-[state=open]:animate-fade-in" />
        <RDialog.Content onCloseAutoFocus={returnFocus} className={cn('fixed inset-y-0 right-0 z-50 flex w-full flex-col bg-surface shadow-[var(--shadow-pop)] data-[state=open]:animate-slide-in', width)}>
          <div className="flex items-start justify-between gap-4 border-b border-ink-100 px-5 py-4">
            <div className="min-w-0">
              <RDialog.Title className="text-base font-semibold text-ink-900">{title}</RDialog.Title>
              <RDialog.Description className={description ? 'mt-0.5 text-[13px] text-ink-500' : 'sr-only'}>
                {description ?? 'Panel'}
              </RDialog.Description>
            </div>
            <RDialog.Close asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Close">
                <X className="h-4 w-4" />
              </Button>
            </RDialog.Close>
          </div>
          <div className="scroll-thin min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t border-ink-100 px-5 py-3">{footer}</div>}
        </RDialog.Content>
      </RDialog.Portal>
    </RDialog.Root>
  )
}

/** Confirmation for consequential actions; optionally requires a written reason. */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  tone = 'primary',
  requireReason,
  reasonLabel = 'Reason',
  onConfirm,
  loading,
}: {
  open: boolean
  onOpenChange: (o: boolean) => void
  title: ReactNode
  description?: ReactNode
  confirmLabel?: string
  tone?: 'primary' | 'danger'
  requireReason?: boolean
  reasonLabel?: string
  onConfirm: (reason: string) => void | Promise<void>
  loading?: boolean
}) {
  const [reason, setReason] = useState('')
  const ok = !requireReason || reason.trim().length >= 5
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setReason('')
        onOpenChange(o)
      }}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant={tone} disabled={!ok} loading={loading} onClick={() => onConfirm(reason.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {description && <div className="text-sm text-ink-600">{description}</div>}
      {requireReason && (
        <Textarea
          className="mt-3"
          wrapperClassName="mt-3"
          label={reasonLabel}
          hint="At least 5 characters. Recorded in the audit trail."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
        />
      )}
    </Dialog>
  )
}

export const Menu = RMenu.Root
export const MenuTrigger = RMenu.Trigger

export function MenuContent({ children, align = 'end' }: { children: ReactNode; align?: 'start' | 'end' | 'center' }) {
  return (
    <RMenu.Portal>
      <RMenu.Content
        align={align}
        sideOffset={6}
        className="z-50 min-w-[200px] rounded-xl border border-ink-200 bg-surface p-1 shadow-[var(--shadow-pop)] data-[state=open]:animate-fade-in"
      >
        {children}
      </RMenu.Content>
    </RMenu.Portal>
  )
}

export function MenuItem({ children, onSelect, danger, icon, disabled }: { children: ReactNode; onSelect?: () => void; danger?: boolean; icon?: ReactNode; disabled?: boolean }) {
  return (
    <RMenu.Item
      disabled={disabled}
      onSelect={onSelect}
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-2 text-sm outline-none select-none data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50 data-[highlighted]:bg-ink-100',
        danger ? 'text-rose-700' : 'text-ink-800',
      )}
    >
      {icon && <span className="text-ink-500">{icon}</span>}
      {children}
    </RMenu.Item>
  )
}

export const MenuSeparator = () => <RMenu.Separator className="my-1 h-px bg-ink-100" />
export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <RMenu.Label className="px-2.5 py-1.5 text-xs font-medium text-ink-500">{children}</RMenu.Label>
)

export const Popover = RPopover.Root
export const PopoverTrigger = RPopover.Trigger
export function PopoverContent({ children, className, align = 'end' }: { children: ReactNode; className?: string; align?: 'start' | 'end' | 'center' }) {
  return (
    <RPopover.Portal>
      <RPopover.Content
        align={align}
        sideOffset={8}
        className={cn('z-50 rounded-xl border border-ink-200 bg-surface shadow-[var(--shadow-pop)] outline-none data-[state=open]:animate-fade-in', className)}
      >
        {children}
      </RPopover.Content>
    </RPopover.Portal>
  )
}

export function Tabs({
  value,
  onValueChange,
  tabs,
  children,
  className,
}: {
  value: string
  onValueChange: (v: string) => void
  tabs: { value: string; label: ReactNode; count?: number }[]
  children?: ReactNode
  className?: string
}) {
  return (
    <RTabs.Root value={value} onValueChange={onValueChange} className={className}>
      <RTabs.List className="scroll-thin flex gap-1 overflow-x-auto border-b border-ink-200" aria-label="Sections">
        {tabs.map((t) => (
          <RTabs.Trigger
            key={t.value}
            value={t.value}
            className="relative -mb-px flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2.5 text-sm font-medium whitespace-nowrap text-ink-500 transition hover:text-ink-800 data-[state=active]:border-brand-600 data-[state=active]:text-brand-800"
          >
            {t.label}
            {t.count !== undefined && (
              <span className="rounded-full bg-ink-100 px-1.5 py-px text-[11px] font-semibold text-ink-600 tabular">{t.count}</span>
            )}
          </RTabs.Trigger>
        ))}
      </RTabs.List>
      {/* Pages that render their own content still need a tabpanel per tab so aria-controls resolves. */}
      {children ?? tabs.map((t) => <RTabs.Content key={t.value} value={t.value} forceMount hidden />)}
    </RTabs.Root>
  )
}
export const TabPanel = RTabs.Content

export function Tooltip({ content, children, side = 'top' }: { content: ReactNode; children: ReactNode; side?: 'top' | 'bottom' | 'left' | 'right' }) {
  return (
    <RTooltip.Root delayDuration={250}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content side={side} sideOffset={6} className="z-[60] max-w-xs rounded-lg bg-ink-900 px-2.5 py-1.5 text-xs text-ink-50 shadow-lg">
          {content}
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  )
}
export const TooltipProvider = RTooltip.Provider
